/**
 * Patrona — één chatvenster om met het brein te praten.
 *
 * De indeling komt uit het bestaande ontwerp (`docs/patrona/ontwerp-referentie.tsx`), maar
 * dat draait op een ander bouwsjabloon. Overgenomen zijn: de indeling, de
 * licht/donker-schakelaar, de kop, de berichtenweergave, de invoerbalk en de pulserende
 * puntjes. De kleuren komen hier als token uit `styles/tokens/` (ADR-043); de bewegingen en
 * de donkere variant staan in `styles/patrona.css`.
 *
 * Eén wezenlijk verschil met de referentie: die stuurde alleen de namen van de bijlagen mee
 * (`files: [namen]`) en praatte rechtstreeks met de webhook. Hier gaan de bestanden zélf
 * mee, via multipart naar onze eigen backend — die leest ze uit en praat met de webhook. De
 * sleutel komt zo nooit in de browser.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Paperclip, Mic, Send, RotateCcw, X, FileText } from "lucide-react";
import { adminApiInstance, formatApiError } from "../../lib/adminApi";

const THEMA_SLEUTEL = "patrona-thema";
const SESSIE_SLEUTEL = "patrona-sessie-id";

/* De backend wacht 180 seconden op Patrona; de browser wacht net iets langer, anders
   breekt axios af vóórdat de server zijn eigen nette melding kan geven. */
const WACHTTIJD_MS = 185_000;

const MAX_BIJLAGEN = 5;
const MAX_BYTES = 10 * 1024 * 1024;
/* Dezelfde lijst als de backend afdwingt. Hier staat hij om het meteen te kunnen zeggen,
   niet om de controle te vervangen — de server blijft de poort. */
const TOEGESTAAN = ["pdf", "docx", "txt", "md", "csv", "png", "jpg", "jpeg", "webp"];

function nieuweCode() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `s-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}

function extensieVan(naam) {
  return (naam || "").includes(".") ? naam.split(".").pop().toLowerCase() : "";
}

/** Meldingen in gewone taal; de gebruiker hoeft niet te weten hoe het heet. */
function keurBijlage(bestand, alAantal) {
  if (alAantal >= MAX_BIJLAGEN) {
    return `U kunt maximaal ${MAX_BIJLAGEN} bestanden tegelijk meesturen.`;
  }
  if (!TOEGESTAAN.includes(extensieVan(bestand.name))) {
    return `'${bestand.name || "dit bestand"}' kan ik niet gebruiken. Stuur een pdf, Word-bestand, tekstbestand, csv of een afbeelding.`;
  }
  if (bestand.size > MAX_BYTES) {
    return `'${bestand.name}' is ${(bestand.size / 1024 / 1024).toFixed(1)} MB en daarmee te groot. Het maximum is 10 MB per bestand.`;
  }
  return null;
}

export function PatronaVenster({ onClose }) {
  const [berichten, setBerichten] = useState([]);
  const [invoer, setInvoer] = useState("");
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);
  const [sessieId, setSessieId] = useState(null);
  const [neemtOp, setNeemtOp] = useState(false);
  const [spraakKan, setSpraakKan] = useState(false);
  const [bijlagen, setBijlagen] = useState([]);
  const [sleept, setSleept] = useState(false);

  const sleepDiepte = useRef(0);
  const herkenningRef = useRef(null);
  const scrollRef = useRef(null);
  const invoerRef = useRef(null);
  const bestandRef = useRef(null);
  const bijlagenRef = useRef([]);
  const invoerWaardeRef = useRef("");

  // --- Thema: onthouden in localStorage, net als in de referentie ----------
  // Beginwaarde meteen uit localStorage: met een leeseffect ernaast zou het schrijfeffect
  // in dezelfde ronde eerst "licht" over de bewaarde keuze heen zetten.
  const [thema, setThema] = useState(() => {
    const bewaard = window.localStorage.getItem(THEMA_SLEUTEL);
    return bewaard === "donker" || bewaard === "licht" ? bewaard : "licht";
  });

  useEffect(() => {
    window.localStorage.setItem(THEMA_SLEUTEL, thema);
  }, [thema]);

  // --- Sessiecode: één keer aanmaken, tot "Nieuw gesprek" -------------------
  useEffect(() => {
    const bewaard = window.sessionStorage.getItem(SESSIE_SLEUTEL);
    if (bewaard) {
      setSessieId(bewaard);
    } else {
      const code = nieuweCode();
      window.sessionStorage.setItem(SESSIE_SLEUTEL, code);
      setSessieId(code);
    }
  }, []);

  // --- Spraakherkenning van de browser -------------------------------------
  useEffect(() => {
    const Herkenning = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Herkenning) return undefined;      // knop blijft verborgen
    setSpraakKan(true);

    const herkenning = new Herkenning();
    herkenning.lang = "nl-NL";
    herkenning.continuous = true;
    herkenning.interimResults = true;

    let definitief = "";
    herkenning.onresult = (gebeurtenis) => {
      let tussentijds = "";
      for (let i = gebeurtenis.resultIndex; i < gebeurtenis.results.length; i += 1) {
        const stuk = gebeurtenis.results[i][0].transcript;
        if (gebeurtenis.results[i].isFinal) definitief += stuk;
        else tussentijds += stuk;
      }
      setInvoer((definitief + tussentijds).trimStart());
    };
    herkenning.onerror = () => {
      setNeemtOp(false);
      setFout("Ik kon u niet verstaan. Probeer het opnieuw of typ uw vraag.");
    };
    herkenning.onend = () => setNeemtOp(false);
    herkenning.onstart = () => {
      // Wat er al stond blijft staan; de spraak schrijft erachteraan. Uit de ref, niet uit
      // een state-updater: die moet zuiver zijn en draait onder StrictMode twee keer.
      const huidig = invoerWaardeRef.current;
      definitief = huidig ? `${huidig} ` : "";
    };

    herkenningRef.current = herkenning;
    return () => {
      herkenning.onresult = null;
      herkenning.onend = null;
      herkenning.onerror = null;      // anders meldt een late fout zich na het opruimen
      try { herkenning.abort(); } catch { /* niets */ }
    };
  }, []);

  function wisselOpname() {
    const herkenning = herkenningRef.current;
    if (!herkenning) return;
    if (neemtOp) {
      herkenning.stop();
      setNeemtOp(false);
      return;
    }
    setFout(null);
    try {
      herkenning.start();
      setNeemtOp(true);
    } catch {
      setNeemtOp(false);
    }
  }

  // --- Nieuw gesprek --------------------------------------------------------
  function nieuwGesprek() {
    if (herkenningRef.current && neemtOp) {
      try { herkenningRef.current.stop(); } catch { /* niets */ }
    }
    setNeemtOp(false);
    setBerichten([]);
    setInvoer("");
    zetBijlagen([]);
    setFout(null);
    setBezig(false);
    window.sessionStorage.removeItem(SESSIE_SLEUTEL);
    const code = nieuweCode();
    window.sessionStorage.setItem(SESSIE_SLEUTEL, code);
    setSessieId(code);
    invoerRef.current?.focus();
  }

  // De ref loopt mee met het invoerveld, zodat de spraakherkenning de huidige tekst kan
  // lezen zonder een verouderde closure.
  useEffect(() => { invoerWaardeRef.current = invoer; }, [invoer]);

  // Zolang het venster open staat scrollt de pagina eronder niet mee: anders schuift het
  // beheerscherm weg terwijl je in het gesprek scrollt.
  useEffect(() => {
    const vorige = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = vorige; };
  }, []);

  // Escape sluit het venster. Een venster dat over het scherm ligt hoort met het
  // toetsenbord weg te kunnen, niet alleen met de muis.
  useEffect(() => {
    function bijToetsUp(gebeurtenis) {
      if (gebeurtenis.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", bijToetsUp);
    return () => window.removeEventListener("keydown", bijToetsUp);
  }, [onClose]);

  // --- Meescrollen ----------------------------------------------------------
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [berichten, bezig]);

  // --- Bijlagen -------------------------------------------------------------
  // De keuring gebeurt bewust búiten de updater van setBijlagen: een updater hoort zuiver
  // te zijn, en StrictMode draait hem twee keer. Een setFout daarbinnen zou dan tweemaal
  // vuren — hier toevallig zonder gevolg, maar het is het soort broosheid dat later bijt.
  /* De stand staat ook in een ref, en die wordt synchroon bijgewerkt.
     Waarom: twee acties binnen dezelfde tick (een plak- en een sleep-gebeurtenis, of twee
     drops kort na elkaar) zien in een gewone closure allebei nog de óude lijst, en dan
     telt de bovengrens van vijf niet meer. De keuring zelf blijft buiten de state-updater,
     want die moet zuiver zijn: StrictMode draait hem twee keer. */
  function zetBijlagen(nieuw) {
    bijlagenRef.current = nieuw;
    setBijlagen(nieuw);
  }

  const voegToe = useCallback((binnenkomend) => {
    if (!binnenkomend.length) return;
    const huidig = bijlagenRef.current;
    const aanvaard = [];
    let bezwaar = null;
    for (const bestand of binnenkomend) {
      bezwaar = keurBijlage(bestand, huidig.length + aanvaard.length);
      if (bezwaar) break;
      aanvaard.push(bestand);
    }
    if (aanvaard.length) {
      const nieuw = [...huidig, ...aanvaard];
      bijlagenRef.current = nieuw;
      setBijlagen(nieuw);
    }
    setFout(bezwaar);
  }, []);

  function bijLoslaten(gebeurtenis) {
    gebeurtenis.preventDefault();
    sleepDiepte.current = 0;
    setSleept(false);
    voegToe(Array.from(gebeurtenis.dataTransfer?.files ?? []));
  }

  function bijPlakken(gebeurtenis) {
    const geplakt = Array.from(gebeurtenis.clipboardData?.files ?? []);
    if (geplakt.length) {
      gebeurtenis.preventDefault();
      voegToe(geplakt);
    }
  }

  // --- Versturen ------------------------------------------------------------
  async function versturen() {
    const tekst = invoer.trim();
    if (!tekst || !sessieId || bezig) return;

    // De opname stoppen: hij loopt door (`continuous`), en de buffer in de herkenning
    // wordt alleen bij starten gelijkgezet. Zonder dit plakt de volgende zin aan de vorige
    // vraag vast en gaat die er samen uit.
    if (neemtOp) {
      try { herkenningRef.current?.stop(); } catch { /* niets */ }
      setNeemtOp(false);
    }

    const meegestuurd = bijlagen;
    const namen = meegestuurd.map((b) => b.name || "geplakt bestand");
    const berichtId = nieuweCode();
    setBerichten((vorige) => [...vorige, {
      id: berichtId,
      rol: "gebruiker",
      inhoud: tekst,
      bijlagen: namen,
    }]);

    const formulier = new FormData();
    formulier.append("bericht", tekst);
    formulier.append("sessie_id", sessieId);
    // Het bestand zélf gaat mee, niet alleen de naam — dat is het hele punt.
    meegestuurd.forEach((bestand) => formulier.append("bijlagen", bestand, bestand.name || "bijlage"));

    setInvoer("");
    zetBijlagen([]);
    setBezig(true);
    setFout(null);

    try {
      // Eigen tijdslimiet: de gedeelde axios-instance staat op 15 seconden, en dat is
      // voor een brein met bijlagen te kort. De backend mag er 180 over doen; de browser
      // wacht net iets langer, zodat de melding van de server wint van die van axios.
      const antwoord = await adminApiInstance().post(
        "/api/admin/patrona/chat",
        formulier,
        { timeout: WACHTTIJD_MS },
      );
      const tekstTerug = typeof antwoord?.data?.antwoord === "string"
        ? antwoord.data.antwoord
        : "Patrona gaf deze keer geen antwoord terug.";
      setBerichten((vorige) => [...vorige, { id: nieuweCode(), rol: "patrona", inhoud: tekstTerug }]);
    } catch (err) {
      // Alles terugzetten zoals het was. Het bericht zei "probeer het zo nog eens", maar
      // zonder dit moest de gebruiker zijn vraag opnieuw typen en alle bijlagen opnieuw
      // opzoeken — juist bij bijlagen, waar het wachten het langst duurt en een fout het
      // waarschijnlijkst is.
      setBerichten((vorige) => vorige.filter((b) => b.id !== berichtId));
      setInvoer(tekst);
      // Samenvoegen, niet overschrijven: het venster neemt tijdens het wachten gewoon
      // nieuwe bijlagen aan, en die zouden hier stil verdwijnen.
      zetBijlagen([...meegestuurd, ...bijlagenRef.current].slice(0, MAX_BIJLAGEN));

      // Eerst de kale melding van de backend; die is al in gewone taal geschreven.
      // formatApiError plakt er `error_code: ` voor, en dat hoort een gebruiker niet te
      // zien — die is bedoeld voor het logboek, niet voor het scherm.
      const netjes = err?.response?.data?.detail?.message;
      setFout(typeof netjes === "string" && netjes ? netjes : formatApiError(err));
    } finally {
      setBezig(false);
    }
  }

  function bijToets(gebeurtenis) {
    if (gebeurtenis.key === "Enter" && !gebeurtenis.shiftKey) {
      gebeurtenis.preventDefault();
      versturen();
    }
  }

  // --- Opmaak ---------------------------------------------------------------
  const RONDEKNOP = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: "var(--patrona-knop)", height: "var(--patrona-knop)", flexShrink: 0,
    borderRadius: "var(--radius-pil)", border: "1px solid var(--border-subtle)",
    background: "var(--surface-card)", color: "var(--text-soft)", cursor: "pointer",
    transition: "color var(--duur-snel) var(--ease-standaard), border-color var(--duur-snel) var(--ease-standaard)",
  };
  const KOPJE = {
    font: "var(--text-label)", fontSize: "var(--fs-xs)", textTransform: "uppercase",
    letterSpacing: "0.06em", color: "var(--text-soft)", fontWeight: "var(--fw-medium)",
  };

  return (
    <div
      className="patrona-overlay"
      data-testid="patrona-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Praat met Patrona"
        className={`patrona patrona-paneel${thema === "donker" ? " patrona--donker" : ""}`}
        data-testid="patrona-page"
      >
        <button
          type="button"
          onClick={() => onClose?.()}
          aria-label="Patrona sluiten"
          data-testid="patrona-sluiten"
          className="patrona-sluitknop"
        >
          <X size={20} aria-hidden="true" />
        </button>
      <header style={{
        margin: "0 auto", width: "100%", maxWidth: "var(--patrona-venster-max)",
        paddingBottom: "var(--ruimte-5)", textAlign: "center", flexShrink: 0,
      }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <div
            role="group"
            aria-label="Weergave"
            style={{
              display: "inline-flex", alignItems: "center", gap: "var(--ruimte-1)",
              borderRadius: "var(--radius-pil)", border: "1px solid var(--border-subtle)",
              background: "var(--surface-card)", padding: "2px",
            }}
          >
            {[["licht", "Licht"], ["donker", "Donker"]].map(([waarde, label]) => (
              <button
                key={waarde}
                type="button"
                onClick={() => setThema(waarde)}
                aria-pressed={thema === waarde}
                data-testid={`patrona-thema-${waarde}`}
                style={{
                  ...KOPJE,
                  borderRadius: "var(--radius-pil)", border: "none", cursor: "pointer",
                  padding: "var(--ruimte-2) var(--ruimte-4)",
                  background: thema === waarde ? "var(--actie)" : "transparent",
                  color: thema === waarde ? "var(--text-inverse)" : "var(--text-soft)",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <h1 style={{
          font: "var(--text-h2)", color: "var(--text-strong)", marginTop: "var(--ruimte-4)",
        }}>
          Praat met Patrona
        </h1>
        <p style={{ marginTop: "var(--ruimte-3)", fontSize: "var(--fs-body)", color: "var(--text-soft)" }}>
          Stel een vraag. Het brein antwoordt direct.
        </p>
        <button
          type="button"
          onClick={nieuwGesprek}
          disabled={bezig}
          data-testid="patrona-nieuw-gesprek"
          style={{
            ...KOPJE,
            marginTop: "var(--ruimte-5)", display: "inline-flex", alignItems: "center",
            gap: "var(--ruimte-3)", borderRadius: "var(--radius-pil)",
            border: "1px solid var(--border-subtle)", background: "var(--surface-card)",
            padding: "var(--ruimte-3) var(--ruimte-6)",
            /* Uit tijdens het wachten: anders wist dit het gesprek terwijl er nog een
               antwoord onderweg is, en valt dat straks in een leeg venster. */
            cursor: bezig ? "not-allowed" : "pointer", opacity: bezig ? 0.5 : 1,
          }}
        >
          <RotateCcw size={14} aria-hidden="true" />
          Nieuw gesprek
        </button>
      </header>

      <main
        onDragEnter={(e) => { e.preventDefault(); sleepDiepte.current += 1; setSleept(true); }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => {
          e.preventDefault();
          sleepDiepte.current -= 1;
          if (sleepDiepte.current <= 0) { sleepDiepte.current = 0; setSleept(false); }
        }}
        onDrop={bijLoslaten}
        onPaste={bijPlakken}
        data-testid="patrona-venster"
        style={{
          position: "relative", margin: "0 auto", display: "flex", width: "100%",
          maxWidth: "var(--patrona-venster-max)", minHeight: 0, flex: 1,
          flexDirection: "column", overflow: "hidden",
          borderRadius: "var(--radius-vlak)",
          border: `1px solid ${sleept ? "var(--actie)" : "var(--border-subtle)"}`,
          background: "var(--surface-card)", boxShadow: "var(--elevation-card)",
          transition: "border-color var(--duur-snel) var(--ease-standaard)",
        }}
      >
        {sleept ? (
          <div
            data-testid="patrona-sleepvlak"
            style={{
              position: "absolute", inset: 0, zIndex: 10, display: "flex",
              alignItems: "center", justifyContent: "center",
              borderRadius: "var(--radius-vlak)", background: "var(--surface-page)",
              opacity: 0.92, pointerEvents: "none",
            }}
          >
            <p style={KOPJE}>Laat los om toe te voegen</p>
          </div>
        ) : null}

        <div
          ref={scrollRef}
          aria-live="polite"
          data-testid="patrona-berichten"
          style={{
            minHeight: 0, flex: 1, overflowY: "auto",
            display: "flex", flexDirection: "column", gap: "var(--ruimte-6)",
            padding: "var(--ruimte-6)",
          }}
        >
          {berichten.length === 0 ? (
            <div
              className="patrona-verschijn"
              data-testid="patrona-leeg"
              style={{
                flex: 1, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", textAlign: "center",
              }}
            >
              <span style={{ font: "var(--text-h3)", color: "var(--text-strong)" }}>Hola Patron,</span>
              <span style={{ marginTop: "var(--ruimte-3)", fontSize: "var(--fs-body)", color: "var(--text-soft)" }}>
                Waar kan ik je mee van dienst zijn?
              </span>
            </div>
          ) : null}

          {berichten.map((bericht) => (
            <div
              key={bericht.id}
              className="patrona-bericht-in"
              data-testid={`patrona-bericht-${bericht.rol}`}
              style={{ display: "flex", justifyContent: bericht.rol === "gebruiker" ? "flex-end" : "flex-start" }}
            >
              <div style={{
                maxWidth: "var(--patrona-bel-max)", borderRadius: "var(--radius)",
                padding: "var(--ruimte-5) var(--ruimte-6)",
                background: bericht.rol === "gebruiker" ? "var(--actie)" : "var(--neutraal-100)",
                color: bericht.rol === "gebruiker" ? "var(--text-inverse)" : "var(--text-body)",
              }}>
                {bericht.rol === "patrona" ? (
                  <p style={{ ...KOPJE, marginBottom: "var(--ruimte-2)" }}>Het brein</p>
                ) : null}
                <p style={{ whiteSpace: "pre-wrap", fontSize: "var(--fs-body)", lineHeight: "var(--lh-tekst, 1.6)" }}>
                  {bericht.inhoud}
                </p>
                {bericht.bijlagen?.length ? (
                  <p style={{
                    marginTop: "var(--ruimte-3)", fontSize: "var(--fs-xs)", opacity: 0.85,
                  }}>
                    {bericht.bijlagen.length === 1 ? "Bijlage: " : "Bijlagen: "}
                    {bericht.bijlagen.join(", ")}
                  </p>
                ) : null}
              </div>
            </div>
          ))}

          {bezig ? (
            <div className="patrona-verschijn" data-testid="patrona-wacht" style={{ display: "flex", justifyContent: "flex-start" }}>
              <div style={{
                borderRadius: "var(--radius)", background: "var(--neutraal-100)",
                padding: "var(--ruimte-5) var(--ruimte-6)",
              }}>
                <p style={{ ...KOPJE, marginBottom: "var(--ruimte-2)" }}>Het brein</p>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--ruimte-3)", padding: "var(--ruimte-2) 0" }}>
                  {[0, 200, 400].map((vertraging) => (
                    <span
                      key={vertraging}
                      className="patrona-punt"
                      style={{
                        height: 8, width: 8, borderRadius: "var(--radius-pil)",
                        background: "var(--text-soft)", animationDelay: `${vertraging}ms`,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {fout ? (
            <div className="patrona-verschijn" style={{ display: "flex", justifyContent: "center" }}>
              <div
                role="alert"
                data-testid="patrona-fout"
                style={{
                  borderRadius: "var(--radius)", border: "1px solid var(--alarm-rand)",
                  background: "var(--alarm-zacht)", padding: "var(--ruimte-4) var(--ruimte-6)",
                  textAlign: "center", fontSize: "var(--fs-sm)", color: "var(--text-alarm)",
                }}
              >
                {fout}
              </div>
            </div>
          ) : null}
        </div>

        <div style={{
          borderTop: "1px solid var(--border-subtle)", background: "var(--surface-card)",
          padding: "var(--ruimte-5) var(--ruimte-6)",
        }}>
          {bijlagen.length > 0 ? (
            <div data-testid="patrona-bijlagen" style={{
              marginBottom: "var(--ruimte-4)", display: "flex", flexWrap: "wrap", gap: "var(--ruimte-3)",
            }}>
              {bijlagen.map((bestand, index) => (
                <span
                  key={`${bestand.name}-${index}`}
                  data-testid={`patrona-bijlage-${index}`}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "var(--ruimte-3)",
                    maxWidth: "100%", borderRadius: "var(--radius-pil)",
                    border: "1px solid var(--border-subtle)", background: "var(--surface-page)",
                    padding: "var(--ruimte-1) var(--ruimte-5) var(--ruimte-1) var(--ruimte-2)",
                    fontSize: "var(--fs-xs)", color: "var(--text-soft)",
                  }}
                >
                  <span style={{
                    display: "flex", height: 24, width: 24, flexShrink: 0, alignItems: "center",
                    justifyContent: "center", borderRadius: "var(--radius-pil)",
                    background: "var(--neutraal-100)",
                  }}>
                    <FileText size={12} aria-hidden="true" />
                  </span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {bestand.name || "geplakt bestand"}
                  </span>
                  <button
                    type="button"
                    onClick={() => zetBijlagen(bijlagen.filter((_, i) => i !== index))}
                    aria-label={`Verwijder ${bestand.name || "bijlage"}`}
                    data-testid={`patrona-bijlage-weg-${index}`}
                    style={{
                      flexShrink: 0, display: "inline-flex", border: "none",
                      background: "none", color: "var(--text-soft)", cursor: "pointer", padding: 0,
                    }}
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          ) : null}

          <input
            ref={bestandRef}
            type="file"
            multiple
            accept={TOEGESTAAN.map((e) => `.${e}`).join(",")}
            data-testid="patrona-bestandsveld"
            style={{ display: "none" }}
            onChange={(e) => {
              voegToe(Array.from(e.target.files ?? []));
              e.target.value = "";
            }}
          />

          <textarea
            ref={invoerRef}
            value={invoer}
            onChange={(e) => setInvoer(e.target.value)}
            onKeyDown={bijToets}
            /* Geen eigen onPaste: de gebeurtenis borrelt op naar het venster, dat hem al
               afhandelt. Stond hij op beide, dan kwam elke geplakte bijlage er dubbel in
               — en bij drie bestanden weigerde de server het hele bericht. */
            disabled={bezig || !sessieId}
            rows={2}
            aria-label="Uw vraag aan Patrona"
            data-testid="patrona-invoer"
            placeholder="Typ uw vraag, of sleep en plak hier documenten en foto's…"
            style={{
              width: "100%", maxHeight: "12rem", minHeight: "4rem", resize: "none",
              borderRadius: "var(--radius)", border: "1px solid var(--border-strong)",
              background: "var(--surface-page)", color: "var(--text-body)",
              padding: "var(--ruimte-5)", fontFamily: "inherit",
              /* 16px-ondergrens: daaronder zoomt iOS in bij het aantikken. */
              fontSize: "var(--fs-invoer-mobiel)", lineHeight: 1.5,
            }}
          />

          <div style={{ marginTop: "var(--ruimte-4)", display: "flex", alignItems: "center", gap: "var(--ruimte-4)" }}>
            <button
              type="button"
              onClick={() => bestandRef.current?.click()}
              disabled={bezig}
              aria-label="Bestand toevoegen"
              data-testid="patrona-bijlage-knop"
              style={{ ...RONDEKNOP, opacity: bezig ? 0.5 : 1 }}
            >
              <Paperclip size={20} aria-hidden="true" />
            </button>

            <div style={{ flex: 1 }} />

            {spraakKan ? (
              <button
                type="button"
                onClick={wisselOpname}
                disabled={bezig}
                aria-label={neemtOp ? "Stop opname" : "Start opname"}
                aria-pressed={neemtOp}
                data-testid="patrona-microfoon"
                style={{
                  ...RONDEKNOP,
                  borderColor: neemtOp ? "var(--actie)" : "var(--border-subtle)",
                  color: neemtOp ? "var(--actie)" : "var(--text-soft)",
                  opacity: bezig ? 0.5 : 1,
                }}
              >
                <span className={neemtOp ? "patrona-punt" : undefined} style={{ display: "inline-flex" }}>
                  <Mic size={20} aria-hidden="true" />
                </span>
              </button>
            ) : null}

            <button
              type="button"
              onClick={versturen}
              disabled={bezig || !invoer.trim() || !sessieId}
              aria-label="Verstuur bericht"
              data-testid="patrona-versturen"
              style={{
                ...RONDEKNOP,
                border: "none", background: "var(--actie)", color: "var(--text-inverse)",
                opacity: bezig || !invoer.trim() || !sessieId ? 0.5 : 1,
                cursor: bezig || !invoer.trim() ? "not-allowed" : "pointer",
              }}
            >
              <Send size={20} aria-hidden="true" />
            </button>
          </div>

          <p style={{
            marginTop: "var(--ruimte-4)", textAlign: "center",
            fontSize: "var(--fs-xs)", color: "var(--text-soft)",
          }}>
            {neemtOp ? "Ik luister… tik nogmaals op de microfoon om te stoppen" : "Druk op Enter om te versturen"}
          </p>
        </div>
        </main>
      </div>
    </div>
  );
}


/**
 * De route `/admin/patrona`, voor wie er rechtstreeks heen linkt of hem als bladwijzer
 * heeft. Sluiten brengt je terug naar het beheerscherm.
 *
 * Vanuit de zwevende knop opent hetzelfde venster zónder te navigeren: dan komt het over
 * het scherm dat je al open had, en sta je na het sluiten weer waar je was.
 */
export default function PatronaPage() {
  const navigeer = useNavigate();
  return <PatronaVenster onClose={() => navigeer("/admin")} />;
}
